import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {GeometryAttributeData, GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {Operators} from "#template/geometry-processing/geometry-graph"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

// Cross-dim operator: source dim slots are `TAttribute` / `TAttributeSet` (e.g. CurveLike for the
// PointGeometryBoundaryOperator variant). The output dim is bound by the variant's class generic.
export type BoundaryOperatorParameters<TAttribute, TAttributeSet> = {
    name: string
    primaryAttribute: TAttribute | null
    attributeSet: TAttributeSet | null
}

export const boundaryOperatorParametersSchema = <TAttribute, TAttributeSet>(
    attributeSchema: z.ZodMiniType<TAttribute, TAttribute>,
    attributeSetSchema: z.ZodMiniType<TAttributeSet, TAttributeSet>,
) =>
    z.object({
        name: z.string(),
        primaryAttribute: z.nullable(attributeSchema),
        attributeSet: z.nullable(attributeSetSchema),
    })

type BoundaryOperatorInputIndexingDim = Exclude<IndexingDim, 0>
type BoundaryOperatorOutputIndexingDim<TIn extends BoundaryOperatorInputIndexingDim> = TIn extends 2 ? 1 : TIn extends 1 ? 0 : never

function boundaryOperatorOutputIndexing<TIn extends BoundaryOperatorInputIndexingDim>(indexingDim: TIn): BoundaryOperatorOutputIndexingDim<TIn> {
    const outputIndexing = (() => {
        if (indexingDim === 2) return 1
        else if (indexingDim === 1) return 0
        else throw new Error(`Invalid indexing dimension for boundary operator: ${indexingDim}`)
    })()
    return outputIndexing as BoundaryOperatorOutputIndexingDim<TIn>
}

function applyBoundaryOperator<TIn extends BoundaryOperatorInputIndexingDim>(
    primaryAttribute: GeometryAttributeData<TIn>,
    attributeSet: GeometryAttributeSetData<TIn>,
): GeometryAttributeSetData<BoundaryOperatorOutputIndexingDim<TIn>> {
    const primaryAttrRefData = primaryAttribute.ref
    const attrRefDataSet = geometryAttributeSetDataToGeometryAttributeRefDataSet(attributeSet)

    const boundaryAttrRefDataSet = applyGeometryOperator(
        (primaryAttrRef: AttributeRef, attrRefSet: AttributeRefSet) => Operators.boundary(primaryAttrRef, attrRefSet),
        primaryAttrRefData,
        attrRefDataSet,
    )

    const outDim = boundaryOperatorOutputIndexing(attributeSet.dim)
    return geometryAttributeRefDataSetToGeometryAttributeSetData(boundaryAttrRefDataSet, outDim)
}

export abstract class GeometryBoundaryOperatorNode<
    out TIn extends BoundaryOperatorInputIndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<TIn> | null>,
    out TAttributeSet extends EvaluableTemplateNode<GeometryAttributeSetData<TIn> | null>,
> extends GeometryAttributeSetNode<BoundaryOperatorOutputIndexingDim<TIn>, BoundaryOperatorParameters<TAttribute, TAttributeSet>> {
    protected override hasValidParameters(
        parameters: BoundaryOperatorParameters<TAttribute, TAttributeSet>,
    ): parameters is BoundaryOperatorParameters<TAttribute, TAttributeSet> & NonNullableParameters<BoundaryOperatorParameters<TAttribute, TAttributeSet>> {
        return parameters.primaryAttribute !== null && parameters.attributeSet !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<BoundaryOperatorParameters<TAttribute, TAttributeSet>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<BoundaryOperatorOutputIndexingDim<TIn>> | null>; invalidator?: BuilderInlet<null>} {
        const {primaryAttribute, attributeSet} = parameters

        const [primaryAttributeDataValid, primaryAttributeDataInvalid] = scope.branch(evaluator.evaluate(primaryAttribute))
        const [attributeSetDataValid, attributeSetDataInvalid] = scope.branch(evaluator.evaluate(attributeSet))

        const geometryAttributeSetData = scope.pureLambda(
            scope.tuple(primaryAttributeDataValid, attributeSetDataValid),
            ([primaryAttributeData, attributeSetData]) => applyBoundaryOperator(primaryAttributeData, attributeSetData),
            "applyBoundaryOperator",
        )

        return {
            evaluatedGeometryData: geometryAttributeSetData,
            invalidator: scope.phi(primaryAttributeDataInvalid, attributeSetDataInvalid),
        }
    }
}
